/**
 * ProjectLifecycleView — `/projects/$projectId/lifecycle`
 *
 * Tab Lifecycle del proyecto. Alineada al prototipo (Pantalla 7):
 *  - Header: lifecycle template name + version + methodology + leyenda.
 *  - Timeline horizontal de phases (LifecycleTimeline).
 *  - Drill-in expandible de la phase seleccionada (PhaseDrillIn) con la
 *    tabla de artifacts de esa phase.
 *
 * Datos:
 *  - GET /api/v1/projects/{id}/lifecycle  → phases + artifact_counts
 *  - GET /api/v1/projects/{id}/canon      → artifacts agrupados por phase (API path conserva /canon)
 *
 * Selección por defecto:
 *  1. Phase con status='in_progress' (la "activa")
 *  2. Si no hay, la primera phase no skipped por phase_order
 *  3. Si todo está skipped, primera phase del array
 *
 * El name + version del lifecycle template se derivan del
 * `project.metadata.lifecycle_template_key/version` que metimos en el
 * wizard (B.5.e.2). Si el metadata no los tiene (proyecto creado antes
 * o por API directa), caemos a "—" en eyebrow.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../api/client'
import {
  fetchProjectLifecycle,
  type ProjectPhase,
} from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'
import { LifecycleTimeline } from '../../components/projects/LifecycleTimeline'
import { PhaseDrillIn } from '../../components/projects/PhaseDrillIn'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { useBreadcrumbExtension, useProjectContext } from './ProjectShell'

export function ProjectLifecycleView() {
  const { t } = useTranslation(['projects', 'errors'])
  const { project } = useProjectContext()
  const { accessToken } = useAccessToken()

  useBreadcrumbExtension([{ label: t('shell.tabs.lifecycle') }])

  // ─── Queries ─────────────────────────────────────────────────────────────
  const lifecycleQuery = useQuery({
    queryKey: ['projects', project.id, 'lifecycle'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchProjectLifecycle(accessToken, project.id)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  // El drill-in pide su propio listado de artifacts filtrado por phase
  // (B.6.c.1.5) — ya no necesitamos fetch de officialGroups acá.
  const phases = useMemo(() => lifecycleQuery.data?.phases ?? [], [lifecycleQuery.data])

  // ─── Selected phase ───────────────────────────────────────────────────────
  // El usuario puede sobrescribir el default; cuando lo hace, el state
  // local manda. Si todavía no eligió nada, derivamos del lifecycle.
  const [overrideCode, setOverrideCode] = useState<string | null>(null)

  const defaultSelectedCode = useMemo(
    () => pickDefaultPhase(phases),
    [phases],
  )
  const selectedCode = overrideCode ?? defaultSelectedCode
  const selectedPhase = useMemo(
    () => phases.find((p) => p.phase_code === selectedCode) ?? null,
    [phases, selectedCode],
  )

  // ─── Header data ──────────────────────────────────────────────────────────
  const meta = (project.metadata ?? {}) as Record<string, unknown>
  const templateKey =
    typeof meta['lifecycle_template_key'] === 'string'
      ? (meta['lifecycle_template_key'] as string)
      : '—'
  const templateVersion =
    typeof meta['lifecycle_template_version'] === 'string'
      ? (meta['lifecycle_template_version'] as string)
      : ''
  const templateName = humanizeTemplateKey(templateKey)
  const phaseCount = phases.filter((p) => p.contract_mode !== 'skipped').length

  // ─── Render ───────────────────────────────────────────────────────────────
  if (lifecycleQuery.isLoading) {
    return (
      <ScrollPage padding="tight">
        <p className="font-mono text-xs text-ink-subtle">
          {t('lifecycle.loading')}
        </p>
      </ScrollPage>
    )
  }
  if (lifecycleQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={lifecycleQuery.error} />
      </ScrollPage>
    )
  }
  if (phases.length === 0) {
    // Project legacy sin phases materializadas. La migración 0008
    // (B.5.e.3) backfilla esto en upgrade; si por alguna razón aún
    // queda un proyecto sin phases, mostramos un empty state honesto.
    return (
      <ScrollPage padding="tight">
        <EmptyLifecycle />
      </ScrollPage>
    )
  }

  return (
    <ScrollPage padding="tight">
      <div className="max-w-7xl">
      {/* Header */}
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1">
            {t('lifecycle.header_eyebrow', {
              templateName,
              version: templateVersion,
            })}
          </p>
          <h2 className="font-display text-2xl font-semibold text-ink">
            {t('lifecycle.header_title', {
              phaseCount,
              methodology: t(
                `methodology.${project.methodology}` as 'methodology.waterfall',
              ),
            })}
          </h2>
          <p className="text-sm text-ink-muted font-body mt-1">
            {t('lifecycle.header_subtitle')}
          </p>
        </div>
        <Legend />
      </header>

      {/* Timeline */}
      <LifecycleTimeline
        phases={phases}
        selectedCode={selectedCode}
        onSelect={(p: ProjectPhase) => setOverrideCode(p.phase_code)}
      />

      {/* Drill-in */}
      {selectedPhase ? (
        <PhaseDrillIn
          phase={selectedPhase}
          project={project}
          allPhases={phases}
        />
      ) : null}
      </div>
    </ScrollPage>
  )
}

// ─── Empty state ────────────────────────────────────────────────────────────
function EmptyLifecycle() {
  const { t } = useTranslation('projects')
  return (
    <div className="max-w-2xl mx-auto rounded-md border border-dashed border-line bg-surface p-10 text-center">
      <h3 className="font-display text-lg text-ink">
        {t('lifecycle.empty_title')}
      </h3>
      <p className="mt-2 font-body text-sm text-ink-muted">
        {t('lifecycle.empty_body')}
      </p>
    </div>
  )
}

// ─── Legend ────────────────────────────────────────────────────────────────
function Legend() {
  const { t } = useTranslation('projects')
  return (
    <div className="flex items-center gap-3 text-xs text-ink-muted flex-wrap">
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-official" />
        {t('lifecycle.legend.approved')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-review" />
        {t('lifecycle.legend.in_progress')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-ink-subtle" />
        {t('lifecycle.legend.not_started')}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="w-2.5 h-2.5 rounded-full bg-surface-muted border border-line"
          aria-hidden
        />
        {t('lifecycle.legend.skipped')}
      </span>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
/** in_progress → primera no skipped → primera. */
function pickDefaultPhase(phases: ProjectPhase[]): string | null {
  if (phases.length === 0) return null
  const ordered = [...phases].sort((a, b) => a.phase_order - b.phase_order)
  const inProgress = ordered.find((p) => p.status === 'in_progress')
  if (inProgress) return inProgress.phase_code
  const firstActive = ordered.find((p) => p.contract_mode !== 'skipped')
  if (firstActive) return firstActive.phase_code
  return ordered[0]?.phase_code ?? null
}

/** `tpl-lifecycle-hsl` → "HIVA Standard Lifecycle".
 * `tpl-lifecycle-lite` → "Lite SDLC".
 * `tpl-lifecycle-compliance` → "Compliance-heavy".
 * Cualquier otro → keyboard. */
function humanizeTemplateKey(key: string): string {
  switch (key) {
    case 'tpl-lifecycle-hsl':
      return 'HIVA Standard Lifecycle'
    case 'tpl-lifecycle-lite':
      return 'Lite SDLC'
    case 'tpl-lifecycle-compliance':
      return 'Compliance-heavy'
    default:
      return key
  }
}

// ─── ErrorBox ───────────────────────────────────────────────────────────────
function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
